// ─── Spreadsheet formula engine ────────────────────────────────────────────
//
// A small, dependency-free evaluator for Excel-style formulas (cells whose raw
// text starts with "="). It supports cell references (A1, $A$1), ranges
// (A1:B10), the usual operators (+ - * / ^ & % and comparisons), and a library
// of common functions (SUM, AVERAGE, IF, VLOOKUP-free essentials, text/maths).
//
// `evaluateGrid` walks an entire sheet, resolving cell-to-cell dependencies
// with memoisation and circular-reference detection, and returns both the
// display strings (what each cell should render) and a parallel numeric grid
// (used by the status-bar aggregates).

// Errors are represented as values (objects) rather than thrown, so functions
// like IFERROR can inspect and recover from them.
export interface FormulaError {
  __error: string;
}

export type CellValue = number | string | boolean | FormulaError | CellValue[];

function isErr(v: CellValue): v is FormulaError {
  return typeof v === "object" && v !== null && "__error" in v;
}

function ERR(code: string): FormulaError {
  return { __error: code };
}

// ─── Numeric parsing (shared with the status bar) ─────────────────────────

// Parse a cell's display text into a number, tolerating thousands separators,
// currency symbols, %, and (parenthesised) negatives the way Excel does.
// Returns null for non-numeric text.
export function parseNumeric(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  let s = trimmed.replace(/[,\s$₹€£¥]/g, "");
  let isPercent = false;
  if (s.endsWith("%")) {
    isPercent = true;
    s = s.slice(0, -1);
  }
  if (/^\(.*\)$/.test(s)) s = `-${s.slice(1, -1)}`;
  if (s === "" || !/^-?\d*\.?\d+(?:[eE][+-]?\d+)?$/.test(s)) return null;
  const n = Number(s);
  if (Number.isNaN(n)) return null;
  return isPercent ? n / 100 : n;
}

// Format a numeric result for display, trimming binary floating-point noise
// (0.1 + 0.2 → "0.3" rather than "0.30000000000000004").
function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return n > 0 ? "#NUM!" : "#NUM!";
  if (Number.isInteger(n)) return String(n);
  return String(parseFloat(n.toPrecision(12)));
}

// ─── Column-letter ⇄ index helpers ────────────────────────────────────────

function lettersToIndex(letters: string): number {
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

// ─── Tokeniser ─────────────────────────────────────────────────────────────

interface Coord {
  row: number;
  col: number;
  // Optional sheet name for cross-sheet references (e.g. Sheet2!A1).
  sheet?: string;
}

// Try to read a bare cell reference ($A$1) at `pos`. Returns the coords and the
// index just past the reference, or null if there isn't one.
function matchCellRef(
  input: string,
  pos: number,
): { col: number; row: number; end: number } | null {
  const len = input.length;
  let p = pos;
  if (input[p] === "$") p += 1;
  let letters = "";
  while (p < len && isAlpha(input[p])) {
    letters += input[p];
    p += 1;
  }
  let q = p;
  if (input[q] === "$") q += 1;
  let digits = "";
  while (q < len && isDigit(input[q])) {
    digits += input[q];
    q += 1;
  }
  if (letters.length > 0 && digits.length > 0) {
    return {
      col: lettersToIndex(letters.toUpperCase()),
      row: parseInt(digits, 10) - 1,
      end: q,
    };
  }
  return null;
}

type Token =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "bool"; v: boolean }
  | { t: "cell"; v: Coord }
  | { t: "func"; v: string }
  | { t: "op"; v: string }
  | { t: "lparen" }
  | { t: "rparen" }
  | { t: "comma" }
  | { t: "colon" };

function isDigit(ch: string): boolean {
  return ch >= "0" && ch <= "9";
}

function isAlpha(ch: string): boolean {
  return (ch >= "A" && ch <= "Z") || (ch >= "a" && ch <= "z") || ch === "_";
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  const n = input.length;
  let i = 0;
  while (i < n) {
    const ch = input[i];
    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i += 1;
    } else if (ch === '"') {
      // Quoted string; "" is an escaped quote.
      let j = i + 1;
      let s = "";
      let closed = false;
      while (j < n && !closed) {
        if (input[j] === '"') {
          if (input[j + 1] === '"') {
            s += '"';
            j += 2;
          } else {
            closed = true;
          }
        } else {
          s += input[j];
          j += 1;
        }
      }
      tokens.push({ t: "str", v: s });
      i = closed ? j + 1 : j;
    } else if (isDigit(ch) || (ch === "." && isDigit(input[i + 1] ?? ""))) {
      let j = i;
      while (j < n && (isDigit(input[j]) || input[j] === ".")) j += 1;
      if (input[j] === "e" || input[j] === "E") {
        j += 1;
        if (input[j] === "+" || input[j] === "-") j += 1;
        while (j < n && isDigit(input[j])) j += 1;
      }
      tokens.push({ t: "num", v: parseFloat(input.slice(i, j)) });
      i = j;
    } else if (isAlpha(ch) || ch === "$" || ch === "'") {
      // A cell reference ($A$1), a sheet-qualified reference (Sheet2!A1 or
      // 'My Sheet'!A1), or a name (function / TRUE / FALSE).
      let sheetName: string | undefined;
      let j = i;
      if (ch === "'") {
        // Quoted sheet name: '...'! — quote followed by "!" makes it a prefix.
        let k = i + 1;
        let nm = "";
        while (k < n && input[k] !== "'") {
          nm += input[k];
          k += 1;
        }
        if (input[k] === "'" && input[k + 1] === "!") {
          sheetName = nm;
          j = k + 2;
        } else {
          j = i + 1; // malformed — skip the stray quote
        }
      } else {
        // Bareword sheet prefix: WORD! (e.g. Sheet2!).
        let k = i;
        let word = "";
        while (
          k < n &&
          (isAlpha(input[k]) || isDigit(input[k]) || input[k] === ".")
        ) {
          word += input[k];
          k += 1;
        }
        if (word.length > 0 && input[k] === "!") {
          sheetName = word;
          j = k + 1;
        }
      }

      const cellRef = matchCellRef(input, j);
      if (cellRef) {
        tokens.push({
          t: "cell",
          v: { col: cellRef.col, row: cellRef.row, sheet: sheetName },
        });
        i = cellRef.end;
      } else if (sheetName === undefined && ch !== "'") {
        // A plain name: function call or TRUE / FALSE (letters only).
        let k = i;
        let letters = "";
        while (k < n && isAlpha(input[k])) {
          letters += input[k];
          k += 1;
        }
        if (letters.length > 0) {
          const up = letters.toUpperCase();
          if (up === "TRUE" || up === "FALSE") {
            tokens.push({ t: "bool", v: up === "TRUE" });
          } else {
            tokens.push({ t: "func", v: up });
          }
          i = k;
        } else {
          i += 1; // stray "$"
        }
      } else {
        // Sheet prefix with no valid cell after it — skip past it.
        i = j > i ? j : i + 1;
      }
    } else {
      const two = input.substr(i, 2);
      if (two === "<=" || two === ">=" || two === "<>") {
        tokens.push({ t: "op", v: two });
        i += 2;
      } else if ("+-*/^&=<>%".includes(ch)) {
        tokens.push({ t: "op", v: ch });
        i += 1;
      } else if (ch === "(") {
        tokens.push({ t: "lparen" });
        i += 1;
      } else if (ch === ")") {
        tokens.push({ t: "rparen" });
        i += 1;
      } else if (ch === ",") {
        tokens.push({ t: "comma" });
        i += 1;
      } else if (ch === ":") {
        tokens.push({ t: "colon" });
        i += 1;
      } else {
        // Unknown character — skip.
        i += 1;
      }
    }
  }
  return tokens;
}

// ─── Parser (recursive descent → AST) ──────────────────────────────────────

type Node =
  | { type: "num"; value: number }
  | { type: "str"; value: string }
  | { type: "bool"; value: boolean }
  | { type: "cell"; ref: Coord }
  | { type: "range"; start: Coord; end: Coord }
  | { type: "unary"; op: string; operand: Node }
  | { type: "percent"; operand: Node }
  | { type: "binary"; op: string; left: Node; right: Node }
  | { type: "func"; name: string; args: Node[] };

class Parser {
  private tokens: Token[];

  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private next(): Token | undefined {
    const t = this.tokens[this.pos];
    this.pos += 1;
    return t;
  }

  parse(): Node {
    const node = this.parseComparison();
    return node;
  }

  private parseComparison(): Node {
    let left = this.parseConcat();
    let tok = this.peek();
    while (
      tok &&
      tok.t === "op" &&
      ["=", "<>", "<", ">", "<=", ">="].includes(tok.v)
    ) {
      this.next();
      const right = this.parseConcat();
      left = { type: "binary", op: tok.v, left, right };
      tok = this.peek();
    }
    return left;
  }

  private parseConcat(): Node {
    let left = this.parseAdditive();
    let tok = this.peek();
    while (tok && tok.t === "op" && tok.v === "&") {
      this.next();
      const right = this.parseAdditive();
      left = { type: "binary", op: "&", left, right };
      tok = this.peek();
    }
    return left;
  }

  private parseAdditive(): Node {
    let left = this.parseMultiplicative();
    let tok = this.peek();
    while (tok && tok.t === "op" && (tok.v === "+" || tok.v === "-")) {
      this.next();
      const right = this.parseMultiplicative();
      left = { type: "binary", op: tok.v, left, right };
      tok = this.peek();
    }
    return left;
  }

  private parseMultiplicative(): Node {
    let left = this.parseUnary();
    let tok = this.peek();
    while (tok && tok.t === "op" && (tok.v === "*" || tok.v === "/")) {
      this.next();
      const right = this.parseUnary();
      left = { type: "binary", op: tok.v, left, right };
      tok = this.peek();
    }
    return left;
  }

  private parseUnary(): Node {
    const tok = this.peek();
    if (tok && tok.t === "op" && (tok.v === "-" || tok.v === "+")) {
      this.next();
      return { type: "unary", op: tok.v, operand: this.parseUnary() };
    }
    return this.parsePower();
  }

  private parsePower(): Node {
    const base = this.parsePostfix();
    const tok = this.peek();
    if (tok && tok.t === "op" && tok.v === "^") {
      this.next();
      // Right-associative.
      const exp = this.parseUnary();
      return { type: "binary", op: "^", left: base, right: exp };
    }
    return base;
  }

  private parsePostfix(): Node {
    let node = this.parsePrimary();
    let tok = this.peek();
    while (tok && tok.t === "op" && tok.v === "%") {
      this.next();
      node = { type: "percent", operand: node };
      tok = this.peek();
    }
    return node;
  }

  private parsePrimary(): Node {
    const tok = this.next();
    if (!tok) return { type: "num", value: 0 };
    if (tok.t === "num") return { type: "num", value: tok.v };
    if (tok.t === "str") return { type: "str", value: tok.v };
    if (tok.t === "bool") return { type: "bool", value: tok.v };
    if (tok.t === "lparen") {
      const inner = this.parseComparison();
      if (this.peek()?.t === "rparen") this.next();
      return inner;
    }
    if (tok.t === "cell") {
      // A range is two cell refs separated by a colon.
      if (this.peek()?.t === "colon") {
        this.next();
        const end = this.next();
        if (end && end.t === "cell") {
          return { type: "range", start: tok.v, end: end.v };
        }
      }
      return { type: "cell", ref: tok.v };
    }
    if (tok.t === "func") {
      // Function call when followed by "(", otherwise a bare name → #NAME?.
      if (this.peek()?.t === "lparen") {
        this.next();
        const args: Node[] = [];
        if (this.peek()?.t !== "rparen") {
          args.push(this.parseComparison());
          while (this.peek()?.t === "comma") {
            this.next();
            args.push(this.parseComparison());
          }
        }
        if (this.peek()?.t === "rparen") this.next();
        return { type: "func", name: tok.v, args };
      }
      return { type: "func", name: tok.v, args: [] };
    }
    return { type: "num", value: 0 };
  }
}

// ─── Value coercion helpers ─────────────────────────────────────────────────

function toText(v: CellValue): string {
  if (isErr(v)) return v.__error;
  if (typeof v === "number") return formatNumber(v);
  if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
  if (Array.isArray(v)) return v.length ? toText(v[0]) : "";
  return v;
}

function toNum(v: CellValue): number | FormulaError {
  if (isErr(v)) return v;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (Array.isArray(v)) return v.length ? toNum(v[0]) : ERR("#VALUE!");
  if (v.trim() === "") return 0;
  const p = parseNumeric(v);
  return p === null ? ERR("#VALUE!") : p;
}

function toBool(v: CellValue): boolean | FormulaError {
  if (isErr(v)) return v;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (Array.isArray(v)) return v.length ? toBool(v[0]) : false;
  const s = v.trim().toLowerCase();
  if (s === "" || s === "false" || s === "0") return false;
  if (s === "true") return true;
  const p = parseNumeric(v);
  return p === null ? true : p !== 0;
}

function scalar(v: CellValue | undefined): CellValue {
  if (v === undefined) return "";
  if (Array.isArray(v)) return v.length ? scalar(v[0]) : "";
  return v;
}

// Flatten args (expanding ranges) into a numeric list, propagating any error.
function collectNumbers(vals: CellValue[]): number[] | FormulaError {
  const out: number[] = [];
  const walk = (v: CellValue): FormulaError | null => {
    if (isErr(v)) return v;
    if (Array.isArray(v)) {
      for (const item of v) {
        const e = walk(item);
        if (e) return e;
      }
      return null;
    }
    if (typeof v === "number") {
      out.push(v);
      return null;
    }
    if (typeof v === "string") {
      const p = parseNumeric(v);
      if (p !== null) out.push(p);
      return null;
    }
    // booleans are ignored, matching Excel's treatment of logicals in ranges
    return null;
  };
  for (const v of vals) {
    const e = walk(v);
    if (e) return e;
  }
  return out;
}

function flattenAll(vals: CellValue[]): CellValue[] {
  const out: CellValue[] = [];
  const walk = (v: CellValue): void => {
    if (Array.isArray(v)) v.forEach(walk);
    else out.push(v);
  };
  vals.forEach(walk);
  return out;
}

// ─── Function library ────────────────────────────────────────────────────────

type Fn = (args: CellValue[]) => CellValue;

function reduceNums(
  args: CellValue[],
  seed: number,
  fn: (acc: number, n: number) => number,
): CellValue {
  const ns = collectNumbers(args);
  if (isErr(ns)) return ns;
  return ns.reduce(fn, seed);
}

const FUNCTIONS: Record<string, Fn> = {
  SUM: (args) => reduceNums(args, 0, (a, b) => a + b),
  PRODUCT: (args) => {
    const ns = collectNumbers(args);
    if (isErr(ns)) return ns;
    return ns.length ? ns.reduce((a, b) => a * b, 1) : 0;
  },
  AVERAGE: (args) => {
    const ns = collectNumbers(args);
    if (isErr(ns)) return ns;
    if (!ns.length) return ERR("#DIV/0!");
    return ns.reduce((a, b) => a + b, 0) / ns.length;
  },
  AVG: (args) => FUNCTIONS.AVERAGE(args),
  COUNT: (args) => {
    const ns = collectNumbers(args);
    if (isErr(ns)) return ns;
    return ns.length;
  },
  COUNTA: (args) =>
    flattenAll(args).filter((v) => !(typeof v === "string" && v === "")).length,
  MAX: (args) => {
    const ns = collectNumbers(args);
    if (isErr(ns)) return ns;
    return ns.length ? Math.max(...ns) : 0;
  },
  MIN: (args) => {
    const ns = collectNumbers(args);
    if (isErr(ns)) return ns;
    return ns.length ? Math.min(...ns) : 0;
  },
  MEDIAN: (args) => {
    const ns = collectNumbers(args);
    if (isErr(ns)) return ns;
    if (!ns.length) return ERR("#NUM!");
    const sorted = [...ns].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  },
  ABS: (args) => {
    const v = toNum(scalar(args[0]));
    return isErr(v) ? v : Math.abs(v);
  },
  SQRT: (args) => {
    const v = toNum(scalar(args[0]));
    if (isErr(v)) return v;
    return v < 0 ? ERR("#NUM!") : Math.sqrt(v);
  },
  POWER: (args) => {
    const a = toNum(scalar(args[0]));
    if (isErr(a)) return a;
    const b = toNum(scalar(args[1]));
    if (isErr(b)) return b;
    return a ** b;
  },
  MOD: (args) => {
    const a = toNum(scalar(args[0]));
    if (isErr(a)) return a;
    const b = toNum(scalar(args[1]));
    if (isErr(b)) return b;
    return b === 0 ? ERR("#DIV/0!") : a - b * Math.floor(a / b);
  },
  INT: (args) => {
    const v = toNum(scalar(args[0]));
    return isErr(v) ? v : Math.floor(v);
  },
  ROUND: (args) => {
    const x = toNum(scalar(args[0]));
    if (isErr(x)) return x;
    const d = toNum(scalar(args[1] ?? 0));
    if (isErr(d)) return d;
    const f = 10 ** d;
    return Math.round(x * f) / f;
  },
  ROUNDUP: (args) => {
    const x = toNum(scalar(args[0]));
    if (isErr(x)) return x;
    const d = toNum(scalar(args[1] ?? 0));
    if (isErr(d)) return d;
    const f = 10 ** d;
    return (x < 0 ? -Math.ceil(-x * f) : Math.ceil(x * f)) / f;
  },
  ROUNDDOWN: (args) => {
    const x = toNum(scalar(args[0]));
    if (isErr(x)) return x;
    const d = toNum(scalar(args[1] ?? 0));
    if (isErr(d)) return d;
    const f = 10 ** d;
    return (x < 0 ? -Math.floor(-x * f) : Math.floor(x * f)) / f;
  },
  CEILING: (args) => {
    const v = toNum(scalar(args[0]));
    return isErr(v) ? v : Math.ceil(v);
  },
  FLOOR: (args) => {
    const v = toNum(scalar(args[0]));
    return isErr(v) ? v : Math.floor(v);
  },
  PI: () => Math.PI,
  IF: (args) => {
    const cond = toBool(scalar(args[0]));
    if (isErr(cond)) return cond;
    if (cond) return scalar(args[1] ?? true);
    return args.length > 2 ? scalar(args[2]) : false;
  },
  IFERROR: (args) =>
    isErr(scalar(args[0])) ? scalar(args[1] ?? "") : scalar(args[0]),
  AND: (args) => {
    const flat = flattenAll(args);
    for (const v of flat) {
      const b = toBool(v);
      if (isErr(b)) return b;
      if (!b) return false;
    }
    return true;
  },
  OR: (args) => {
    const flat = flattenAll(args);
    for (const v of flat) {
      const b = toBool(v);
      if (isErr(b)) return b;
      if (b) return true;
    }
    return false;
  },
  NOT: (args) => {
    const b = toBool(scalar(args[0]));
    return isErr(b) ? b : !b;
  },
  CONCAT: (args) => flattenAll(args).map(toText).join(""),
  CONCATENATE: (args) => flattenAll(args).map(toText).join(""),
  LEN: (args) => toText(scalar(args[0])).length,
  UPPER: (args) => toText(scalar(args[0])).toUpperCase(),
  LOWER: (args) => toText(scalar(args[0])).toLowerCase(),
  TRIM: (args) => toText(scalar(args[0])).trim().replace(/\s+/g, " "),
  LEFT: (args) => {
    const s = toText(scalar(args[0]));
    const nn = toNum(scalar(args[1] ?? 1));
    if (isErr(nn)) return nn;
    return s.slice(0, Math.max(0, nn));
  },
  RIGHT: (args) => {
    const s = toText(scalar(args[0]));
    const nn = toNum(scalar(args[1] ?? 1));
    if (isErr(nn)) return nn;
    return nn <= 0 ? "" : s.slice(s.length - nn);
  },
  MID: (args) => {
    const s = toText(scalar(args[0]));
    const start = toNum(scalar(args[1]));
    if (isErr(start)) return start;
    const len = toNum(scalar(args[2]));
    if (isErr(len)) return len;
    return s.slice(Math.max(0, start - 1), Math.max(0, start - 1) + len);
  },
};

// ─── Evaluator ───────────────────────────────────────────────────────────────

interface EvalCtx {
  cell: (row: number, col: number, sheet?: string) => CellValue;
  range: (start: Coord, end: Coord) => CellValue[];
}

function evaluate(node: Node, ctx: EvalCtx): CellValue {
  switch (node.type) {
    case "num":
      return node.value;
    case "str":
      return node.value;
    case "bool":
      return node.value;
    case "cell":
      return ctx.cell(node.ref.row, node.ref.col, node.ref.sheet);
    case "range":
      return ctx.range(node.start, node.end);
    case "unary": {
      const v = toNum(evaluate(node.operand, ctx));
      if (isErr(v)) return v;
      return node.op === "-" ? -v : v;
    }
    case "percent": {
      const v = toNum(evaluate(node.operand, ctx));
      return isErr(v) ? v : v / 100;
    }
    case "binary":
      return evalBinary(node, ctx);
    case "func": {
      const fn = FUNCTIONS[node.name];
      if (!fn) return ERR("#NAME?");
      const args = node.args.map((a) => evaluate(a, ctx));
      return fn(args);
    }
    default:
      return ERR("#ERROR!");
  }
}

function evalBinary(
  node: { op: string; left: Node; right: Node },
  ctx: EvalCtx,
): CellValue {
  const { op } = node;
  const L = evaluate(node.left, ctx);
  const R = evaluate(node.right, ctx);
  if (op === "&") {
    if (isErr(L)) return L;
    if (isErr(R)) return R;
    return toText(L) + toText(R);
  }
  if (["=", "<>", "<", ">", "<=", ">="].includes(op)) {
    return compare(op, L, R);
  }
  const a = toNum(L);
  if (isErr(a)) return a;
  const b = toNum(R);
  if (isErr(b)) return b;
  switch (op) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      return b === 0 ? ERR("#DIV/0!") : a / b;
    case "^":
      return a ** b;
    default:
      return ERR("#ERROR!");
  }
}

function compare(op: string, L: CellValue, R: CellValue): CellValue {
  if (isErr(L)) return L;
  if (isErr(R)) return R;
  let cmp: number;
  if (typeof L === "number" && typeof R === "number") {
    cmp = L < R ? -1 : L > R ? 1 : 0;
  } else {
    const ls = toText(L).toLowerCase();
    const rs = toText(R).toLowerCase();
    cmp = ls < rs ? -1 : ls > rs ? 1 : 0;
  }
  switch (op) {
    case "=":
      return cmp === 0;
    case "<>":
      return cmp !== 0;
    case "<":
      return cmp < 0;
    case ">":
      return cmp > 0;
    case "<=":
      return cmp <= 0;
    case ">=":
      return cmp >= 0;
    default:
      return ERR("#ERROR!");
  }
}

// ─── Whole-sheet evaluation ──────────────────────────────────────────────────

export interface EvaluatedGrid {
  display: string[][];
  numbers: (number | null)[][];
}

export interface WorkbookSheet {
  name: string;
  rows: string[][];
}

// Evaluate the active sheet of a workbook. Non-formula cells display their raw
// text; formula cells (leading "=") display their computed result. Formulas may
// reference other sheets (Sheet2!A1, 'My Sheet'!A1:B2), resolved against the
// supplied `sheets`. The parallel `numbers` grid holds the numeric value of
// each cell (or null), for the status-bar aggregates.
export function evaluateGrid(
  sheets: WorkbookSheet[],
  activeIndex: number,
  colCount: number,
): EvaluatedGrid {
  const cache = new Map<string, CellValue>();
  const visiting = new Set<string>();

  // Resolve a sheet name to its index (case-insensitive), -1 if not found.
  const findSheet = (name: string): number => {
    const target = name.trim().toLowerCase();
    return sheets.findIndex((s) => s.name.trim().toLowerCase() === target);
  };

  const rawAt = (si: number, r: number, c: number): string =>
    String(sheets[si]?.rows[r]?.[c] ?? "");

  const valueAt = (si: number, r: number, c: number): CellValue => {
    if (si < 0 || si >= sheets.length) return ERR("#REF!");
    const rows = sheets[si].rows;
    if (r < 0 || c < 0 || r >= rows.length) return "";
    const key = `${si},${r},${c}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;
    if (visiting.has(key)) return ERR("#CIRC!");

    const raw = rawAt(si, r, c);
    let result: CellValue;
    if (raw === "") {
      result = "";
    } else if (raw[0] === "=") {
      visiting.add(key);
      try {
        const ast = new Parser(tokenize(raw.slice(1))).parse();
        // The formula's "home" sheet is the one it lives in, so unqualified
        // references resolve against `si`.
        result = evaluate(ast, makeCtx(si));
      } catch {
        result = ERR("#ERROR!");
      }
      visiting.delete(key);
    } else {
      // Coerce numeric text to a number so formulas can reference it.
      const p = parseNumeric(raw);
      result = p === null ? raw : p;
    }
    cache.set(key, result);
    return result;
  };

  const makeCtx = (home: number): EvalCtx => ({
    cell: (r, c, sheet) => {
      const idx = sheet != null ? findSheet(sheet) : home;
      if (idx < 0) return ERR("#REF!");
      return valueAt(idx, r, c);
    },
    range: (start, end) => {
      const idx = start.sheet != null ? findSheet(start.sheet) : home;
      if (idx < 0) return [ERR("#REF!")];
      const out: CellValue[] = [];
      const r0 = Math.min(start.row, end.row);
      const r1 = Math.max(start.row, end.row);
      const c0 = Math.min(start.col, end.col);
      const c1 = Math.max(start.col, end.col);
      for (let r = r0; r <= r1; r += 1) {
        for (let c = c0; c <= c1; c += 1) out.push(valueAt(idx, r, c));
      }
      return out;
    },
  });

  const rows = sheets[activeIndex]?.rows ?? [];
  const rowCount = rows.length;
  const display: string[][] = [];
  const numbers: (number | null)[][] = [];
  for (let r = 0; r < rowCount; r += 1) {
    const dRow: string[] = [];
    const nRow: (number | null)[] = [];
    const width = Math.max(colCount, rows[r]?.length ?? 0);
    for (let c = 0; c < width; c += 1) {
      const raw = rawAt(activeIndex, r, c);
      const isFormula = raw[0] === "=";
      const v = valueAt(activeIndex, r, c);
      dRow[c] = isFormula ? toText(v) : raw;
      nRow[c] = typeof v === "number" ? v : null;
    }
    display[r] = dRow;
    numbers[r] = nRow;
  }
  return { display, numbers };
}
